import { el } from '../../lib/dom';
import type { AppState } from '../../store/app';
import { createHistoryScreen, type HistoryScreenDeps } from './history';
import { createSearchScreen, type SearchScreenDeps } from './search';
import { createSettingsScreen, type SettingsScreenDeps } from './settings';
import { createStationsScreen, type StationsScreenDeps } from './stations';
import { createTimerScreen, type TimerScreenDeps } from './timer';

/**
 * Vsi zasloni imajo od faze 5 svoj modul; nadomestkov (`SCAFFOLDS`) ni več.
 * Postaje imajo modul od faze 1, Nastavitve od faze 2, Iskanje od faze 4,
 * Zgodovina in Časovnik od faze 5 (D1).
 */

export type ScreensDeps = StationsScreenDeps &
  SettingsScreenDeps &
  SearchScreenDeps &
  HistoryScreenDeps &
  TimerScreenDeps;

export function createScreens(
  root: HTMLElement,
  deps: ScreensDeps,
): { update(state: AppState): void } {
  root.replaceChildren();

  /**
   * Vsak zaslon ima svoj razred na korenu.
   *
   * Zasloni so v DOM **vsi hkrati** in se skrivajo z `hidden`, zato `.empty`,
   * `.ao-list` in `.ao-row` obstajajo v več izvodih. Brez imena korena bi jih
   * moral vsak izbirnik ločevati po strukturi — in prvi, ki je to zahteval,
   * niso bili ljudje, ampak E2E testi, ki so ob dodanem zaslonu nehali biti
   * enolični.
   */
  const stationsRoot = el('div', { class: 'nw-screen-stations' });
  const searchRoot = el('div', { class: 'nw-search' });
  const historyRoot = el('div', { class: 'nw-screen-history' });
  const timerRoot = el('div', { class: 'nw-screen-timer' });
  const settingsRoot = el('div', { class: 'nw-screen-settings' });
  root.append(stationsRoot, searchRoot, historyRoot, timerRoot, settingsRoot);

  const stations = createStationsScreen(stationsRoot, deps);
  const search = createSearchScreen(searchRoot, deps);
  const history = createHistoryScreen(historyRoot, deps);
  const timer = createTimerScreen(timerRoot, deps);
  const settings = createSettingsScreen(settingsRoot, deps);

  return {
    update(state) {
      const onStations = state.tab === 'stations';
      const onSearch = state.tab === 'search';
      const onHistory = state.tab === 'history';
      const onTimer = state.tab === 'timer';
      const onSettings = state.tab === 'settings';

      stationsRoot.toggleAttribute('hidden', !onStations);
      searchRoot.toggleAttribute('hidden', !onSearch);
      historyRoot.toggleAttribute('hidden', !onHistory);
      timerRoot.toggleAttribute('hidden', !onTimer);
      settingsRoot.toggleAttribute('hidden', !onSettings);

      // Skriti zaslon se vseeno posodobi: ob menjavi zavihka mora biti vsebina
      // že prava, sicer se ob vsakem preklopu vidi utrip stare. Izjema je živi
      // interval Zgodovine — ta se v `manageTicker` ustavi, kadar zavihek ni na
      // vrsti, zato posodobitev skritega zaslona ne pušča utripa v ozadju.
      if (onStations) stations.update(state);
      if (onSearch) search.update(state);
      history.update(state);
      if (onTimer) timer.update(state);
      if (onSettings) settings.update(state);
    },
  };
}
